/**
 * Guarantee — Public-facing 3× ROI guarantee page
 *
 * The single page the CFO reads to understand what they're really buying.
 * Designed to overcome the "is this actually going to save us money?"
 * objection that kills 80% of FinOps deals at the procurement stage.
 *
 * Pairs with the in-app guarantee tracking widget (logged-in users see
 * their own realized vs target).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, CheckCircle2, FileText, ArrowRight, Scale } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { ENGINE_COUNT } from '../constants/product';

export default function Guarantee() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <SEOHead
        title="3× ROI Guarantee — full refund in 90 days"
        description="If CARTIE doesn't save you 3× our subscription fee in your first 90 days, you get a full refund. In writing. No clawbacks. No fine print."
        canonical="https://cartieai.com/guarantee"
        keywords="CARTIE guarantee, 3x ROI, money-back guarantee, LLM cost savings guarantee, FinOps ROI"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "3× ROI Guarantee — CARTIEAI",
            "description": "Contractually-binding pledge: 3× your subscription cost saved in 90 days, or a full refund.",
            "url": "https://cartieai.com/guarantee",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cartieai.com/" },
              { "@type": "ListItem", "position": 2, "name": "3× ROI Guarantee", "item": "https://cartieai.com/guarantee" },
            ],
          },
        ]}
      />

      <header className="border-b border-white/5 px-4 py-3 flex items-center justify-between sticky top-0 z-30 bg-slate-950/90 backdrop-blur">
        <Link to="/" className="text-sm font-bold text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="guarantee-logo">
          ← CARTIE
        </Link>
        <Link to="/login" className="text-xs font-semibold text-slate-400 hover:text-emerald-300 transition-colors">
          Sign in
        </Link>
      </header>

      <section className="px-4 py-14 sm:py-20 max-w-4xl mx-auto" data-testid="guarantee-hero">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest mb-5">
          <ShieldCheck className="w-3 h-3" /> Contractually binding
        </div>
        <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight leading-[1.05]" data-testid="guarantee-h1">
          The <span className="text-emerald-400">3× ROI</span> Guarantee.
        </h1>
        <p className="mt-6 text-lg sm:text-xl text-slate-300 leading-relaxed max-w-2xl">
          If CARTIE doesn&apos;t save you <strong className="text-emerald-300">3× your subscription fee</strong>
          {' '}in your first <strong className="text-white">90 days</strong>, you get a full refund.
          No clawbacks. No fine print. In writing.
        </p>
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px] font-mono" data-testid="guarantee-scope-note">
          <span className="font-bold">SCOPE TODAY:</span> the Auto-Compiler Proxy (AI token savings) — guarantee active. Cloud-side guarantee ships with the Code Tracer · <Link to="/roadmap" className="underline underline-offset-2 hover:text-amber-200">Q2 2026</Link>.
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/connect" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm transition-all hover:-translate-y-0.5 shadow-lg shadow-emerald-500/30" data-testid="guarantee-cta-connect">
            Start saving · 60-second setup <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="mailto:Harinathmekala@cartieai.com?subject=CARTIE%20Pilot%20—%20Talk%20to%20a%20human" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-white/15 hover:border-emerald-500/40 text-white text-sm font-semibold transition-all" data-testid="guarantee-cta-talk">
            Talk to a human first
          </a>
        </div>
      </section>

      {/* How the math works */}
      <section className="px-4 py-12 border-y border-white/5 bg-slate-900/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2 text-white" data-testid="how-math-h2">How the math actually works.</h2>
          <p className="text-sm text-slate-400 mb-8 max-w-2xl leading-relaxed">
            Most FinOps tools claim savings they can&apos;t prove. We attribute every dollar
            to a specific engine, a specific incident, and a specific code change you can audit.
          </p>

          <div className="space-y-4">
            <MathRow
              n="1"
              title="We measure realized savings — not theoretical."
              body="Every booking is tied to a real action: a merged PR, a deleted resource, a re-sized instance, or a cached LLM call. We don't count 'recommendations'. We count merged fixes that show up in your next bill."
              testid="math-row-1"
            />
            <MathRow
              n="2"
              title="The clock starts the day your data lands."
              body="Day 0 = the day our connector successfully ingests your first AWS/GCP/Azure bill. Not your contract signature. Not your kickoff call."
              testid="math-row-2"
            />
            <MathRow
              n="3"
              title="3× means 3× — on the cumulative subscription you've paid by day 90."
              body="If you've paid $9,000 in subscription fees over 90 days, we need to book ≥ $27,000 in realized savings by that date. The dashboard ticks live so you (and your CFO) always know where you stand."
              testid="math-row-3"
            />
            <MathRow
              n="4"
              title="If we miss, you tell us and we refund. Full stop."
              body="No 'extension period', no 'let us try harder', no clawing back free months. You email Harinathmekala@cartieai.com on day 91. We refund within 5 business days."
              testid="math-row-4"
            />
          </div>
        </div>
      </section>

      {/* Why we can offer it */}
      <section className="px-4 py-12 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2 text-white" data-testid="why-h2">Why we can offer something this strong.</h2>
          <p className="text-sm text-slate-400 mb-8 max-w-2xl leading-relaxed">
            Because the 27-engine portfolio actually closes the loop — it doesn&apos;t stop at dashboards.
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            <Pillar
              title="Trace"
              body="Cost spikes get mapped back to the exact line of code (or terraform block, or LLM call site) that caused them."
              testid="pillar-trace"
            />
            <Pillar
              title="Fix"
              body="The fix ships as a GitHub PR — reviewable, mergeable, auditable. Your engineers stay in their existing workflow."
              testid="pillar-fix"
            />
            <Pillar
              title="Book"
              body="After merge, we watch the next 30 days of billing and credit the realized savings to the incident. CFO has the receipts."
              testid="pillar-book"
            />
            <Pillar
              title="Auto-cut"
              body="The Auto-Compiler Proxy reduces AI token spend at runtime — savings flow in even before your team merges the first PR."
              testid="pillar-autocut"
            />
          </div>
        </div>
      </section>

      {/* What's NOT included */}
      <section className="px-4 py-12 border-b border-white/5 bg-slate-900/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2 text-white" data-testid="exclusions-h2">What the guarantee doesn&apos;t cover. (Honest list.)</h2>
          <p className="text-sm text-slate-400 mb-6 leading-relaxed">
            We&apos;d rather tell you upfront than fight over it in 90 days.
          </p>
          <ul className="space-y-3 text-sm text-slate-300" data-testid="exclusions-list">
            <ExcItem text={(<>Reservations & commit purchases (we can recommend them, but you have to actually sign the AWS/GCP contract — that&apos;s on you).</>)} />
            <ExcItem text={(<>Workloads you explicitly mark &quot;do not optimize&quot; (some teams want zero autonomy in prod — fine, but those savings don&apos;t count toward 3×).</>)} />
            <ExcItem text={(<>Acts of cloud (AWS surprise outage, GCP price re-jig, an OpenAI rate-limit change). We re-baseline and the clock resets.</>)} />
            <ExcItem text={(<>Refunds are pro-rated for legitimately consumed services if you cancel mid-period; the guarantee math is over the full 90 days.</>)} />
          </ul>
        </div>
      </section>

      {/* CTA + contract */}
      <section className="px-4 py-14 max-w-4xl mx-auto">
        <div className="rounded-2xl border border-emerald-500/30 bg-slate-950 bg-gradient-to-br from-emerald-500/10 via-slate-950 to-slate-950 p-6 sm:p-8" data-testid="guarantee-bottom-cta">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/40 shrink-0">
              <Scale className="w-6 h-6 text-slate-950" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Want it in writing first?</h3>
              <p className="text-sm text-slate-300 mt-2 leading-relaxed">
                We&apos;ll send the full Master Services Agreement with the 3× ROI clause boxed
                and highlighted before you sign anything. No friction, no &quot;contact sales&quot; gating —
                just email and we send it the same day.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href="mailto:Harinathmekala@cartieai.com?subject=Send%20me%20the%20MSA%20with%20the%203x%20guarantee" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm transition-all hover:-translate-y-0.5" data-testid="cta-email-msa">
                  <FileText className="w-4 h-4" /> Email me the MSA
                </a>
                <Link to="/connect" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/15 hover:border-emerald-500/40 text-white text-sm font-semibold transition-all" data-testid="cta-start-anyway">
                  Just start saving →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="px-4 py-6 text-center text-xs text-slate-500 border-t border-white/5">
        <Link to="/" className="text-emerald-400 hover:underline">Home</Link> ·
        {' '}<Link to="/patent-portfolio" className="text-emerald-400 hover:underline">All {ENGINE_COUNT} engines</Link> ·
        {' '}<Link to="/security" className="text-emerald-400 hover:underline">Security</Link>
      </footer>
    </div>
  );
}

function MathRow({ n, title, body, testid }) {
  return (
    <div className="flex gap-4 items-start" data-testid={testid}>
      <div className="w-10 h-10 rounded-lg bg-slate-900 border border-emerald-500/30 flex items-center justify-center text-emerald-300 font-mono font-bold text-base shrink-0">{n}</div>
      <div className="min-w-0">
        <div className="text-base font-bold text-white">{title}</div>
        <div className="text-sm text-slate-400 mt-1 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function Pillar({ title, body, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/60 p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-mono font-bold mb-2">{title}</div>
      <div className="text-sm text-white font-bold mb-1.5">{title === 'Auto-cut' ? 'Runtime savings, day 1.' : `${title} → loop closes.`}</div>
      <div className="text-xs text-slate-400 leading-relaxed">{body}</div>
    </div>
  );
}

function ExcItem({ text }) {
  return (
    <li className="flex gap-3 items-start">
      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
      <span className="leading-relaxed">{text}</span>
    </li>
  );
}
